import { z } from "zod";
import { idSchema } from "./common.js";

// --- Mailboxes (provisioned by administrators) ------------------------------------------------------

/** An extra address that delivers into a mailbox; the mailbox can send as it too. */
export const mailboxAliasSchema = z.object({ id: idSchema, address: z.email() });
export type MailboxAlias = z.infer<typeof mailboxAliasSchema>;

export const mailboxSchema = z.object({
  id: idSchema,
  userId: idSchema,
  domainId: idSchema,
  address: z.email(),
  displayName: z.string(),
  quotaBytes: z.number().int().nonnegative(),
  aliases: z.array(mailboxAliasSchema),
  createdAt: z.iso.datetime(),
});
export type Mailbox = z.infer<typeof mailboxSchema>;

export const localPartSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/, {
    message:
      "Letters, digits, dots, dashes and underscores; must start and end with a letter or digit",
  });

export const mailboxCreateRequestSchema = z.object({
  userId: idSchema,
  domainId: idSchema,
  localPart: localPartSchema,
});
export type MailboxCreateRequest = z.infer<typeof mailboxCreateRequestSchema>;

export const mailboxCreatedSchema = z.object({
  mailbox: mailboxSchema,
  /** Shown once; used by phone and desktop mail apps over IMAP and SMTP. */
  appPassword: z.string(),
});
export type MailboxCreated = z.infer<typeof mailboxCreatedSchema>;

export const mailboxListSchema = z.object({ mailboxes: z.array(mailboxSchema) });
export type MailboxList = z.infer<typeof mailboxListSchema>;

export const aliasCreateRequestSchema = z.object({
  domainId: idSchema,
  localPart: localPartSchema,
});
export type AliasCreateRequest = z.infer<typeof aliasCreateRequestSchema>;

// --- Group addresses ---------------------------------------------------------------------------

/** A group address such as team@: mail sent to it goes to every member mailbox. */
export const mailGroupSchema = z.object({
  id: idSchema,
  domainId: idSchema,
  address: z.email(),
  name: z.string(),
  memberIds: z.array(idSchema),
  createdAt: z.iso.datetime(),
});
export type MailGroup = z.infer<typeof mailGroupSchema>;

export const mailGroupListSchema = z.object({ groups: z.array(mailGroupSchema) });
export type MailGroupList = z.infer<typeof mailGroupListSchema>;

const groupNameSchema = z.string().trim().min(1).max(100);
const groupMembersSchema = z.array(idSchema).min(1).max(200);

export const mailGroupCreateRequestSchema = z.object({
  domainId: idSchema,
  localPart: localPartSchema,
  name: groupNameSchema,
  memberIds: groupMembersSchema,
});
export type MailGroupCreateRequest = z.infer<typeof mailGroupCreateRequestSchema>;

export const mailGroupUpdateRequestSchema = z.object({
  name: groupNameSchema.optional(),
  memberIds: groupMembersSchema.optional(),
});
export type MailGroupUpdateRequest = z.infer<typeof mailGroupUpdateRequestSchema>;

export const mailAccountSchema = z.object({
  /** Null when the signed-in user has no mailbox yet. */
  mailbox: mailboxSchema.nullable(),
});
export type MailAccount = z.infer<typeof mailAccountSchema>;

// --- Connecting phones and desktop mail apps ---------------------------------------------------------

const serverSchema = z.object({
  host: z.string(),
  port: z.number().int(),
  /** "tls" encrypts from the first byte (993, 465); "starttls" upgrades a plain connection (587). */
  security: z.enum(["tls", "starttls"]),
});

/** What a mail, calendar or contacts app needs to connect to the signed-in user's mailbox. */
export const mailClientSettingsSchema = z.object({
  address: z.string(),
  username: z.string(),
  imap: serverSchema,
  smtp: z.array(serverSchema),
  /** Base URL for CalDAV and CardDAV apps such as DAVx5. */
  davUrl: z.string(),
});
export type MailClientSettings = z.infer<typeof mailClientSettingsSchema>;

/** Creating an app password asks for the account password again, as Google does. */
export const appPasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(1024),
});
export type AppPasswordRequest = z.infer<typeof appPasswordRequestSchema>;
export const appPasswordSchema = z.object({ appPassword: z.string() });
export type AppPassword = z.infer<typeof appPasswordSchema>;

// --- Reading and writing mail ------------------------------------------------------------------------

export const mailFolderRoleSchema = z
  .enum(["inbox", "sent", "drafts", "trash", "junk", "archive"])
  .nullable();

export const mailFolderSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: mailFolderRoleSchema,
  parentId: z.string().nullable(),
  totalEmails: z.number().int().nonnegative(),
  unreadEmails: z.number().int().nonnegative(),
});
export type MailFolder = z.infer<typeof mailFolderSchema>;

export const mailFolderListSchema = z.object({ folders: z.array(mailFolderSchema) });
export type MailFolderList = z.infer<typeof mailFolderListSchema>;

export const mailAddressSchema = z.object({
  name: z.string().nullable(),
  email: z.string(),
});
export type MailAddress = z.infer<typeof mailAddressSchema>;

export const emailSummarySchema = z.object({
  id: z.string(),
  threadId: z.string(),
  mailboxIds: z.array(z.string()),
  from: z.array(mailAddressSchema),
  to: z.array(mailAddressSchema),
  subject: z.string(),
  preview: z.string(),
  receivedAt: z.iso.datetime(),
  seen: z.boolean(),
  flagged: z.boolean(),
  hasAttachment: z.boolean(),
});
export type EmailSummary = z.infer<typeof emailSummarySchema>;

export const emailDetailSchema = emailSummarySchema.extend({
  cc: z.array(mailAddressSchema),
  replyTo: z.array(mailAddressSchema),
  messageId: z.array(z.string()).nullable(),
  inReplyTo: z.array(z.string()).nullable(),
  textBody: z.string().nullable(),
  htmlBody: z.string().nullable(),
  attachments: z.array(
    z.object({
      blobId: z.string(),
      name: z.string().nullable(),
      type: z.string(),
      size: z.number().int(),
    }),
  ),
});
export type EmailDetail = z.infer<typeof emailDetailSchema>;

export const emailPageQuerySchema = z.object({
  mailboxId: z.string().min(1),
  position: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /** Free-text search within the folder (subject, senders, body). */
  q: z.string().trim().max(200).optional(),
});
export type EmailPageQuery = z.infer<typeof emailPageQuerySchema>;

export const emailPageSchema = z.object({
  emails: z.array(emailSummarySchema),
  total: z.number().int().nonnegative(),
  position: z.number().int().nonnegative(),
});
export type EmailPage = z.infer<typeof emailPageSchema>;

export const emailUpdateRequestSchema = z
  .object({
    seen: z.boolean().optional(),
    flagged: z.boolean().optional(),
    /** Move the message so it lives only in this folder. */
    mailboxId: z.string().min(1).optional(),
  })
  .refine((v) => v.seen !== undefined || v.flagged !== undefined || v.mailboxId !== undefined, {
    message: "Nothing to change",
  });
export type EmailUpdateRequest = z.infer<typeof emailUpdateRequestSchema>;

/** The same change for several messages at once, as the list's selection toolbar sends it. */
export const emailBatchUpdateRequestSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(200),
  change: emailUpdateRequestSchema,
});
export type EmailBatchUpdateRequest = z.infer<typeof emailBatchUpdateRequestSchema>;

const recipientSchema = z.object({
  email: z.email().trim().toLowerCase(),
  name: z.string().trim().max(200).optional(),
});

/** An uploaded file, or an attachment of a draft, included by reference. */
export const attachmentRefSchema = z.object({
  blobId: z.string().min(1).max(300),
  name: z.string().trim().min(1).max(255),
  type: z.string().max(200),
});
export type AttachmentRef = z.infer<typeof attachmentRefSchema>;

export const uploadedAttachmentSchema = attachmentRefSchema.extend({ size: z.number().int() });
export type UploadedAttachment = z.infer<typeof uploadedAttachmentSchema>;

export const sendEmailRequestSchema = z.object({
  /** One of the sender's aliases; the mailbox's own address when absent. */
  from: z.email().optional(),
  to: z.array(recipientSchema).min(1).max(50),
  cc: z.array(recipientSchema).max(50).optional(),
  subject: z.string().trim().max(500),
  text: z.string().max(200_000),
  inReplyTo: z.array(z.string()).max(5).optional(),
  references: z.array(z.string()).max(50).optional(),
  attachments: z.array(attachmentRefSchema).max(20).optional(),
  /** The draft this was written in; it is removed once the message is submitted. */
  draftId: z.string().min(1).optional(),
  /** Hold the message this long so it can be taken back; 0 sends at once. */
  undoSeconds: z.number().int().min(0).max(30).optional(),
});
export type SendEmailRequest = z.infer<typeof sendEmailRequestSchema>;

/** A recipient the mail server refused, with its SMTP reply (e.g. "550 5.1.1 ..."). */
export const refusedRecipientSchema = z.object({ email: z.string(), reason: z.string() });
export type RefusedRecipient = z.infer<typeof refusedRecipientSchema>;

export const sentEmailSchema = z.object({
  id: z.string(),
  submissionId: z.string().nullable(),
  sendAt: z.string().nullable(),
  /** Recipients the server refused; the message went to the others. */
  refused: z.array(refusedRecipientSchema).default([]),
});
export type SentEmail = z.infer<typeof sentEmailSchema>;

/**
 * error.details of a send that left nothing (429 rate_limited, 422 recipients_refused, 400
 * message_rejected): the message is in Drafts under `draftId`, which replaces the draft it was
 * written in. `refused` names the recipients when none was accepted.
 */
export const sendFailureDetailsSchema = z.object({
  draftId: z.string(),
  refused: z.array(refusedRecipientSchema),
});
export type SendFailureDetails = z.infer<typeof sendFailureDetailsSchema>;

export const undoSendResultSchema = z.object({ draftId: z.string() });

/** Drafts may be incomplete: addresses are not checked until the message is sent. */
const draftRecipientSchema = z.object({
  email: z.string().trim().max(320),
  name: z.string().trim().max(200).optional(),
});
export const saveDraftRequestSchema = z.object({
  replaceId: z.string().min(1).nullable().optional(),
  from: z.email().optional(),
  to: z.array(draftRecipientSchema).max(50),
  cc: z.array(draftRecipientSchema).max(50).optional(),
  subject: z.string().max(500),
  text: z.string().max(200_000),
  inReplyTo: z.array(z.string()).max(5).optional(),
  references: z.array(z.string()).max(50).optional(),
  attachments: z.array(attachmentRefSchema).max(20).optional(),
});
export type SaveDraftRequest = z.infer<typeof saveDraftRequestSchema>;
export const savedDraftSchema = z.object({ id: z.string() });

/** What a swipe on a message in the phone list does, as in Gmail's swipe actions. */
export const swipeActionSchema = z.enum(["archive", "delete", "read", "star", "none"]);
export type SwipeAction = z.infer<typeof swipeActionSchema>;

export const mailSettingsSchema = z.object({
  signature: z.string().max(5_000),
  undoSeconds: z.number().int().min(0).max(30),
  // Defaults keep older clients, which do not send them, working.
  swipeLeft: swipeActionSchema.default("archive"),
  swipeRight: swipeActionSchema.default("archive"),
});
export type MailSettings = z.infer<typeof mailSettingsSchema>;

export const recipientQuerySchema = z.object({ q: z.string().trim().min(1).max(100) });

export const recipientSuggestionSchema = z.object({
  name: z.string().nullable(),
  email: z.string(),
  /** Where the address came from: own contacts, workspace colleagues or recent mail. */
  source: z.enum(["contact", "directory", "recent"]),
});
export type RecipientSuggestion = z.infer<typeof recipientSuggestionSchema>;
export const recipientSuggestionListSchema = z.object({
  suggestions: z.array(recipientSuggestionSchema),
});
export type RecipientSuggestionList = z.infer<typeof recipientSuggestionListSchema>;

export const aiMailActionSchema = z.enum(["summarize", "draft_reply"]);
export type AiMailAction = z.infer<typeof aiMailActionSchema>;

export const aiMailRequestSchema = z.object({
  action: aiMailActionSchema,
  /** Free-text guidance for the reply, e.g. "decline politely, suggest next week". */
  instructions: z.string().trim().max(500).optional(),
});
export type AiMailRequest = z.infer<typeof aiMailRequestSchema>;

export const aiMailResultSchema = z.object({
  action: aiMailActionSchema,
  text: z.string(),
  /** AI actions left in the caller's monthly allowance after this one. */
  remainingActions: z.number().int().min(0),
  provider: z.string(),
  model: z.string(),
});
export type AiMailResult = z.infer<typeof aiMailResultSchema>;
